import React from 'react';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  TableHead
} from '../../ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../ui/tooltip';
import IconWrapper from '../../ui/IconWrapper';
import type { IconType } from 'react-icons';
import {
  FiRefreshCw,
  FiTrash2,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiLoader,
  FiCalendar,
} from 'react-icons/fi';
import {
  useProjectionJobs,
  useDeleteProjectionJob,
  useProjectionJobProvider,
  type ProjectionJob,
  type ProjectionJobStatus,
} from '../../../hooks/useFOSProjectionJobs';

// Helper functions
const formatRelativeTime = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
};

const formatDuration = (seconds: number | null | undefined): string => {
  if (seconds === null || seconds === undefined) return '-';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${Math.round(seconds / 3600)}h ${Math.round((seconds % 3600) / 60)}m`;
};

interface StatusBadgeProps {
  status: ProjectionJobStatus;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ status }) => {
  const config: Record<ProjectionJobStatus, { label: string; className: string; icon: IconType }> = {
    pending: {
      label: 'Pending',
      className: 'bg-yellow-100 text-yellow-800',
      icon: FiClock,
    },
    running: {
      label: 'Running',
      className: 'bg-blue-100 text-blue-800 animate-pulse',
      icon: FiLoader,
    },
    succeeded: {
      label: 'Succeeded',
      className: 'bg-green-100 text-green-800',
      icon: FiCheckCircle,
    },
    failed: {
      label: 'Failed',
      className: 'bg-red-100 text-red-800',
      icon: FiXCircle,
    },
    unknown: {
      label: 'Unknown',
      className: 'bg-gray-100 text-gray-600',
      icon: FiClock,
    },
  };

  const { label, className, icon: Icon } = config[status] || config.unknown;

  return (
    <Badge className={`${className} flex items-center gap-1`}>
      <IconWrapper icon={Icon} className={`h-3 w-3 ${status === 'running' ? 'animate-spin' : ''}`} />
      {label}
    </Badge>
  );
};

interface FOSProjectionJobsPanelProps {
  /** Maximum jobs to display */
  limit?: number;
}

/**
 * FOSProjectionJobsPanel - Displays projection job history and management
 *
 * Shows K8s Job history in staging/beta, and a simpler view for Celery in development.
 * Auto-refreshes while jobs are running.
 */
const FOSProjectionJobsPanel: React.FC<FOSProjectionJobsPanelProps> = ({
  limit = 10,
}) => {
  const { isKubernetes, providerLabel } = useProjectionJobProvider();
  const { data: jobsData, isLoading, refetch } = useProjectionJobs(limit);
  const deleteJobMutation = useDeleteProjectionJob();

  const handleDeleteJob = async (jobId: string) => {
    if (!window.confirm('Are you sure you want to delete this job?')) return;
    try {
      await deleteJobMutation.mutateAsync(jobId);
    } catch (error) {
      console.error('Failed to delete job:', error);
    }
  };

  const jobs = jobsData?.jobs ?? [];
  const hasRunningJobs = jobs.some(j => j.status === 'running' || j.status === 'pending');

  // In development (Celery), we can't list jobs, so show a simpler view
  if (!isKubernetes) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {providerLabel}
            </Badge>
            <span className="text-xs text-gray-500">
              Job history not available in development
            </span>
          </div>
        </div>
        <p className="text-sm text-gray-600">
          In development mode, projections run as Celery tasks. Switch to staging or beta to see full job management with Kubernetes Jobs.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with provider info and CronJob schedule */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="text-xs">
            {providerLabel}
          </Badge>
          {jobsData?.cronJob && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge
                    variant="outline"
                    className={`text-xs flex items-center gap-1 ${jobsData.cronJob.suspended ? 'bg-gray-100' : 'bg-purple-50 text-purple-700'}`}
                  >
                    <IconWrapper icon={FiCalendar} className="h-3 w-3" />
                    {jobsData.cronJob.suspended ? 'Scheduled (Suspended)' : 'Scheduled'}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Schedule: {jobsData.cronJob.schedule || 'Not set'}</p>
                  {jobsData.cronJob.lastSchedule && (
                    <p>Last run: {formatRelativeTime(jobsData.cronJob.lastSchedule)}</p>
                  )}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <div className="flex items-center gap-2">
          {hasRunningJobs && (
            <span className="text-xs text-blue-600 flex items-center gap-1">
              <IconWrapper icon={FiLoader} className="h-3 w-3 animate-spin" />
              Auto-refreshing
            </span>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <IconWrapper icon={FiRefreshCw} className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Jobs table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8 text-gray-500">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600 mr-2" />
          Loading jobs...
        </div>
      ) : jobs.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p className="text-sm">No projection jobs found</p>
          <p className="text-xs mt-1">Jobs will appear here when you run projections</p>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[200px]">Job Name</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Started</TableHead>
              <TableHead>Duration</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {jobs.map((job: ProjectionJob) => (
              <TableRow key={job.id}>
                <TableCell className="font-mono text-xs">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="truncate block max-w-[180px]">
                          {job.name}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{job.name}</p>
                        {job.parameters && (
                          <p className="text-xs text-gray-400 mt-1">
                            n={job.parameters.n_neighbors},
                            dist={job.parameters.min_dist},
                            cluster={job.parameters.min_cluster_size}
                          </p>
                        )}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </TableCell>
                <TableCell>
                  <StatusBadge status={job.status} />
                </TableCell>
                <TableCell className="text-sm text-gray-600">
                  {formatRelativeTime(job.startTime || job.createdAt)}
                </TableCell>
                <TableCell className="text-sm text-gray-600">
                  {job.completionTime
                    ? formatDuration(job.durationSeconds)
                    : job.status === 'running'
                    ? 'In progress...'
                    : '-'}
                </TableCell>
                <TableCell className="text-right">
                  {(job.status === 'succeeded' || job.status === 'failed') && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteJob(job.id)}
                      disabled={deleteJobMutation.isPending}
                      title="Delete job"
                    >
                      <IconWrapper icon={FiTrash2} className="h-4 w-4 text-gray-400 hover:text-red-500" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Stats footer */}
      {jobs.length > 0 && (
        <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t">
          <span>
            Showing {jobs.length} of {jobsData?.total ?? jobs.length} jobs
          </span>
          {jobs.filter(j => j.status === 'succeeded').length > 0 && (
            <span>
              Last successful: {formatRelativeTime(
                jobs.find(j => j.status === 'succeeded')?.completionTime
              )}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default FOSProjectionJobsPanel;
